/**
 * Tenant Service
 *
 * Handles all tenant-related API calls:
 * - Tenant CRUD operations
 * - Tenant member management
 * - Tenant switching
 * - Tenant membership validation
 *
 * @see WICL-DOC-051 Post-Login User Onboarding and Tenant Initialization Flow
 * @see WICL-DOC-052 Frontend-Backend Integration Status Matrix
 * @see WICL-DOC-053 Multi-Tenant Initialization Strategy
 */

import { api } from "@/lib/api";
import { type TenantRole, type TenantStatus } from "@/types/tenant";

/**
 * Tenant Type Enum
 */
export type TenantType = "Individual" | "Organization" | "Enterprise";

export type { TenantStatus, TenantRole };

/**
 * Member Status Enum
 */
export type MemberStatus = "Active" | "Invited" | "Suspended" | "Inactive";

/**
 * Tenant DTO
 */
export interface TenantDto {
  id: string;
  name: string;
  slug: string;
  type: TenantType;
  status: TenantStatus;
  ownerId: string;
  subscriptionPlanId?: string;
  quotas: Record<string, unknown>;
  settings: Record<string, unknown>;
  billingAccountId?: string;
  currentUserRole?: TenantRole;
  // Encryption key metadata
  encryptedDataKey?: string | null;
  dataKeyCreatedAt?: string | null;
  dataKeyVersion?: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Tenant Member DTO
 */
export interface TenantMemberDto {
  id: string;
  tenantId: string;
  userId: string;
  userEmail?: string;
  userName?: string;
  role: TenantRole;
  status: MemberStatus;
  permissions: Record<string, unknown>;
  joinedAt: string;
  invitedBy?: string;
  createdAt: string;
}

/**
 * Create Tenant Request
 */
export interface CreateTenantRequest {
  name: string;
  slug: string;
  type: TenantType;
}

/**
 * Update Tenant Request
 */
export interface UpdateTenantRequest {
  name?: string;
  settings?: Record<string, unknown>;
  billingAccountId?: string;
}

/**
 * Add Tenant Member Request
 */
export interface AddTenantMemberRequest {
  userId: string;
  role: TenantRole;
}

/**
 * Get Tenant Members Response
 */
export interface GetTenantMembersResponse {
  members: TenantMemberDto[];
  totalCount: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Switch Tenant Response
 */
export interface SwitchTenantResponse {
  tenantId: string;
  accessToken: string;
  refreshToken: string;
  message: string;
}

/**
 * Validate Tenant Membership Response
 */
export interface ValidateTenantMembershipResponse {
  isMember: boolean;
  canAccess: boolean;
  tenantId: string;
  role?: TenantRole;
  status?: MemberStatus;
  message: string;
}

/**
 * Tenant Service Methods
 */
export const tenantService = {
  /**
   * Get all tenants for the current user
   */
  getUserTenants: (): Promise<TenantDto[]> => {
    return api.get("/tenants");
  },

  /**
   * Get tenant by ID
   */
  getTenantById: (id: string): Promise<TenantDto> => {
    return api.get(`/tenants/${id}`);
  },

  /**
   * Create a new tenant
   */
  createTenant: (data: CreateTenantRequest): Promise<TenantDto> => {
    return api.post("/tenants", data);
  },

  /**
   * Update tenant information
   */
  updateTenant: (id: string, data: UpdateTenantRequest): Promise<TenantDto> => {
    return api.put(`/tenants/${id}`, data);
  },

  /**
   * Delete tenant (soft delete)
   */
  deleteTenant: (id: string): Promise<void> => {
    return api.delete(`/tenants/${id}`);
  },

  /**
   * Get all members of a tenant with pagination
   */
  getTenantMembers: async (
    id: string,
    page: number = 1,
    limit: number = 20,
  ): Promise<GetTenantMembersResponse> => {
    const response = await api.getList<TenantMemberDto>(
      `/tenants/${id}/members`,
      {
        params: { page, limit },
      },
    );
    return {
      members: response.data,
      totalCount: response.pagination.totalItems,
      page: response.pagination.currentPage,
      limit: response.pagination.itemsPerPage,
      totalPages: response.pagination.totalPages,
    };
  },

  /**
   * Add a member to a tenant
   */
  addTenantMember: (
    id: string,
    data: AddTenantMemberRequest,
  ): Promise<TenantMemberDto> => {
    return api.post(`/tenants/${id}/members`, data);
  },

  /**
   * Remove a member from a tenant
   */
  removeTenantMember: (id: string, userId: string): Promise<void> => {
    return api.delete(`/tenants/${id}/members/${userId}`);
  },

  /**
   * Switch the current user's active tenant context
   * Returns new JWT tokens with updated tenant context
   */
  switchTenant: (id: string): Promise<SwitchTenantResponse> => {
    return api.post(`/tenants/${id}/switch`);
  },

  /**
   * Validate if the current user has access to a specific tenant
   */
  validateTenantMembership: (
    tenantId: string,
  ): Promise<ValidateTenantMembershipResponse> => {
    return api.get("/tenants/validate", {
      params: { tenantId },
    });
  },

  /**
   * Generate encryption key for a tenant
   * Creates a new encryption key for securing resource credentials
   */
  generateEncryptionKey: (
    tenantId: string,
  ): Promise<{ message: string; tenantId: string }> => {
    return api.post(`/tenants/${tenantId}/encryption-key`);
  },

  /**
   * Rotate encryption key for a tenant
   * Generates a new key and re-encrypts all resource credentials
   * This is a security best practice for compliance
   */
  rotateEncryptionKey: (
    tenantId: string,
  ): Promise<{
    message: string;
    tenantId: string;
    newKeyVersion: number;
  }> => {
    return api.post(`/tenants/${tenantId}/encryption-key/rotate`);
  },
};
